/**
 * A project document read from anything that crossed a network (§5.25) — the
 * request a publish sends, the row the account area stores, the answer a
 * member's install receives. The Worker refuses what this refuses, and so does
 * the app: there is no second reading.
 *
 * It **refuses rather than repairs**, which is the opposite of the desktop
 * app's own stores. A value clamped on its way into SQLite is a person's typing
 * met halfway; a value clamped on its way into an organization's project is a
 * silent edit of what somebody else published, which every member then pulls.
 * So a field the app would have clamped is a problem named here, with its path.
 *
 * It also refuses what it does not know. A document with a field this version
 * has never heard of was written by a newer app, and applying the half of it
 * this one understands would drop the rest on the next publish — `newer` says
 * so, and the app asks to be updated instead.
 *
 * What comes back on success is canonical: the remote re-derived from the clone
 * URL, every workflow, flow and variable set passed through the sanitizer its
 * editor saves through, and the configuration as `normalizeWorkspaceConfig`
 * keeps it.
 */
import { MAX_FLOWS, MAX_FLOW_NAME_CHARS, sanitizeFlow } from '../api/flow';
import { HTTP_METHODS, MAX_BODY_CHARS, MAX_COLLECTION_ROWS, MAX_FIELD_CHARS, MAX_FOLDER_DEPTH, MAX_SCRIPT_CHARS } from '../api/request';
import type { ApiFlowEdge, ApiFlowNode, FormPart, HttpHeader, HttpMethod, RequestScripts } from '../api/types';
import {
  MAX_ENVIRONMENTS,
  MAX_ENVIRONMENT_NAME_CHARS,
  MAX_VALUE_CHARS,
  MAX_VARIABLES,
  sanitizeVariables
} from '../api/variables';
import { branch, jiraHost } from '../settings/primitives';
import { localServicesInputProblem } from '../settings/localservice';
import { parseRulesRepoRef } from '../rules/repository';
import { SPARSE_CONFIG_KEYS, normalizeWorkspaceConfig } from '../settings/workspace-config';
import { MAX_WORKFLOWS, MAX_WORKFLOW_NAME_CHARS, sanitizeWorkflow } from '../workflow/sanitize';
import type { WorkflowEdge, WorkflowStep } from '../workflow/types';
import {
  ENV_FILE_MODES,
  ENV_SCOPES,
  PROJECT_KINDS,
  RUNTIME_KINDS,
  SHELL_NAME,
  TASK_LANES,
  TASK_SOURCE_KINDS,
  tunnelDomainProblem,
  type EnvFileMode,
  type EnvScope,
  type LaneStatuses,
  type ProjectKind,
  type RuntimeKind,
  type TaskSourceKind
} from './vocabulary';
import {
  MAX_COMMAND_CHARS,
  MAX_DOCUMENT_BYTES,
  MAX_ENV_KEY_CHARS,
  MAX_ENV_VALUE_CHARS,
  MAX_ENV_VARS,
  MAX_WORKSPACE_NAME_CHARS,
  MAX_REL_PATH_CHARS,
  MAX_PROJECTS,
  MAX_PROJECT_NAME_CHARS,
  MAX_REQUEST_DOC_CHARS,
  MAX_TASK_SOURCES,
  WORKSPACE_SCHEMA,
  ROW_ID,
  type DocApi,
  type DocApiEnvironment,
  type DocApiFlow,
  type DocApiFolder,
  type DocApiRequest,
  type DocApiVariable,
  type DocConfig,
  type DocEnvVar,
  type DocProject,
  type DocTaskSource,
  type DocWorkflow,
  type OrgWorkflows,
  type WorkspaceDocument
} from './document';
import { isPlainObject } from './shape';
import { normalizeRemote } from './remote';
import { MAX_SUB_PATH_CHARS, validSubPath } from './subpath';
import { projectNameProblem } from './project-name';

export type ParsedOrgWorkflows = { ok: true; document: OrgWorkflows } | { ok: false; problems: string[] };

export type ParsedDocument =
  | { ok: true; document: WorkspaceDocument }
  | { ok: false; problems: string[]; newer: boolean };

/** Enough to act on; a document with more wrong with it than this is not a draft somebody is fixing. */
const MAX_PROBLEMS = 50;

/**
 * A colour, in either of the two grammars a document has ever carried.
 *
 * Hex is what the first documents held and what an older app still writes.
 * `oklch(L C H)` is what the app's own swatches are (`theme.ts`, and
 * `WORKSPACE_COLORS` beside them, which both pickers now offer): the palette
 * was authored in that space, and a document whose colour cannot say so is one
 * where the app's own six are refused on publish — `At most 9 characters` on a
 * workspace nobody typed a colour into.
 */
const COLOR = /^(#[0-9a-fA-F]{3,8}|oklch\(\s*[\d.]+\s+[\d.]+\s+[\d.]+\s*\))$/;

/** Long enough for the longer of the two: `oklch(0.52 0.11 150)` is 20. */
const MAX_COLOR_CHARS = 32;

const COLOR_PROBLEM = 'A colour is a hex value like #4C8DFF, or an oklch() like the app\u2019s own swatches.';
const ENV_KEY = /^[^\s/=]{1,200}$/;
const MAX_FOLDER_NAME_CHARS = 80;
const MAX_REQUEST_NAME_CHARS = 80;
const MAX_ROWS_IN_A_REQUEST = 200;
const MAX_TASK_TEXT = 200;
const MAX_IMPORT_STATUSES = 50;
const MAX_SORT = 100_000;
/** What a port field holds: `3000`, `:8000`, a variable the start command reads. Not a paragraph. */
const MAX_PORT_CHARS = 32;

/** The size of a document as the Worker stores it and the network carries it. */
export function documentBytes(document: unknown): number {
  return new TextEncoder().encode(JSON.stringify(document)).length;
}

/** A document from its JSON text, refused before parsing when it is over the ceiling. */
export function parseWorkspaceDocumentText(text: string): ParsedDocument {
  if (new TextEncoder().encode(text).length > MAX_DOCUMENT_BYTES) {
    return { ok: false, problems: [`A project document holds at most ${MAX_DOCUMENT_BYTES / 1024} KB.`], newer: false };
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, problems: ['That is not JSON.'], newer: false };
  }
  return parseWorkspaceDocument(value);
}

export function parseWorkspaceDocument(input: unknown): ParsedDocument {
  const p = new Problems();
  if (documentBytes(input) > MAX_DOCUMENT_BYTES) {
    p.add('', `A project document holds at most ${MAX_DOCUMENT_BYTES / 1024} KB — the console of one repository is usually what fills it.`);
    return p.result(null);
  }
  const root = p.object(input, '', ['schema', 'name', 'color', 'repos', 'config', 'workflows', 'taskSources']);
  if (!root) return p.result(null);

  if (typeof root.schema === 'number' && root.schema > WORKSPACE_SCHEMA) {
    p.newer = true;
    p.add('schema', 'This project was published by a newer version of Airlock. Update Airlock to open it.');
    return p.result(null);
  }
  if (root.schema !== WORKSPACE_SCHEMA) p.add('schema', `Expected schema ${WORKSPACE_SCHEMA}.`);

  const name = p.text(root.name, 'name', { min: 1, max: MAX_WORKSPACE_NAME_CHARS, trim: true });
  const color = p.text(root.color, 'color', { max: MAX_COLOR_CHARS });
  if (color !== null && !COLOR.test(color)) p.add('color', COLOR_PROBLEM);

  const repos = parseRepos(p, root.repos);
  const config = parseConfig(p, root.config);
  // Accepted and dropped: workflows were a workspace's until they became the
  // organization's own document (`org/workflows.ts`), and an app installed
  // before that still publishes the key. What it sent is not kept.
  const workflows: DocWorkflow[] = [];
  const taskSources = parseTaskSources(p, root.taskSources);

  if (p.failed || name === null || color === null || !config) return p.result(null);
  return p.result({ schema: WORKSPACE_SCHEMA, name, color, repos, config, workflows, taskSources });
}

/* ── repositories ───────────────────────────────────────────────────────── */

const REPO_KEYS = [
  'id', 'name', 'remote', 'host', 'cloneUrl', 'relPath', 'subPath', 'defaultBranch', 'runtime', 'kind', 'packageManager',
  'installCmd', 'startCmd', 'checkCmd', 'healthCheck', 'port', 'portEnv', 'tunnelDomain', 'tunnelAuto',
  'skipUnchangedInstall', 'color', 'envFile', 'sortOrder', 'env', 'api'
] as const;

function parseRepos(p: Problems, value: unknown): DocProject[] {
  const out: DocProject[] = [];
  const remotes = new Map<string, string>();
  const folders = new Map<string, string>();
  /** Each folder of a repository, by the remote it shares with it (§5.4) — checked once every entry is read. */
  const members: { remote: string; subPath: string; at: string }[] = [];
  for (const [index, raw] of p.list(value, 'repos', MAX_PROJECTS).entries()) {
    const at = `repos[${index}]`;
    const row = p.object(raw, at, REPO_KEYS);
    if (!row) continue;
    const id = p.id(row.id, `${at}.id`, out);
    const name = p.text(row.name, `${at}.name`, { min: 1, max: MAX_PROJECT_NAME_CHARS, trim: true });
    // It becomes a folder on the machine that opens this (`project-name.ts`).
    const named = name === null ? null : projectNameProblem(name);
    if (named && name) p.add(`${at}.name`, named);
    const here = name ? `repos[${name}]` : at;
    const subPath = row.subPath === undefined ? '' : p.text(row.subPath, `${here}.subPath`, { max: MAX_SUB_PATH_CHARS });
    if (subPath && !validSubPath(subPath)) {
      p.add(`${here}.subPath`, 'A folder inside its repository — relative, with forward slashes, and never above it.');
    }
    // A folder shares its repository's remote and place: those are the repository's to be unique about.
    const member = Boolean(subPath);

    const identity = typeof row.cloneUrl === 'string' ? normalizeRemote(row.cloneUrl) : null;
    if (!identity) {
      p.add(`${here}.cloneUrl`, 'This repository has no remote another computer could clone. Push it, then publish.');
    } else if (member) {
      members.push({ remote: identity.remote, subPath: subPath ?? '', at: here });
    } else if (remotes.has(identity.remote)) {
      p.add(`${here}.cloneUrl`, `${identity.remote} is already the remote of ${remotes.get(identity.remote)}.`);
    } else remotes.set(identity.remote, name ?? at);

    const relPath = relativePath(p, row.relPath, `${here}.relPath`);
    if (relPath !== null && !member) {
      if (folders.has(relPath.toLowerCase())) p.add(`${here}.relPath`, `${relPath} is already where ${folders.get(relPath.toLowerCase())} goes.`);
      else folders.set(relPath.toLowerCase(), name ?? at);
    }
    const defaultBranch = typeof row.defaultBranch === 'string' ? branch(row.defaultBranch) : null;
    if (defaultBranch === null) p.add(`${here}.defaultBranch`, 'That is not a branch name git accepts.');

    const portEnv = p.nullableText(row.portEnv, `${here}.portEnv`, 64);
    if (portEnv && !SHELL_NAME.test(portEnv)) p.add(`${here}.portEnv`, 'A variable name — letters, digits and underscores.');
    const tunnelDomain = p.nullableText(row.tunnelDomain, `${here}.tunnelDomain`, 253);
    if (tunnelDomain) {
      const problem = tunnelDomainProblem(tunnelDomain);
      if (problem) p.add(`${here}.tunnelDomain`, problem);
    }
    const color = p.text(row.color, `${here}.color`, { max: MAX_COLOR_CHARS });
    if (color !== null && !COLOR.test(color)) p.add(`${here}.color`, COLOR_PROBLEM);

    const repo = {
      id: id ?? '',
      name: name ?? '',
      remote: identity?.remote ?? '',
      host: identity?.host ?? 'other',
      cloneUrl: identity?.cloneUrl ?? '',
      relPath: relPath ?? '',
      ...(member ? { subPath: subPath ?? '' } : {}),
      defaultBranch: defaultBranch ?? 'main',
      runtime: p.oneOf<RuntimeKind>(row.runtime, `${here}.runtime`, RUNTIME_KINDS) ?? 'unknown',
      kind: p.oneOf<ProjectKind>(row.kind, `${here}.kind`, PROJECT_KINDS) ?? 'unknown',
      packageManager: p.nullableText(row.packageManager, `${here}.packageManager`, 64),
      installCmd: p.nullableText(row.installCmd, `${here}.installCmd`, MAX_COMMAND_CHARS),
      startCmd: p.nullableText(row.startCmd, `${here}.startCmd`, MAX_COMMAND_CHARS),
      checkCmd: p.nullableText(row.checkCmd, `${here}.checkCmd`, MAX_COMMAND_CHARS),
      healthCheck: p.nullableText(row.healthCheck, `${here}.healthCheck`, MAX_COMMAND_CHARS),
      port: p.nullableText(row.port, `${here}.port`, MAX_PORT_CHARS),
      portEnv,
      tunnelDomain: tunnelDomain ? tunnelDomain.trim().toLowerCase() : null,
      tunnelAuto: p.bool(row.tunnelAuto, `${here}.tunnelAuto`) ?? false,
      // Kept only when off: on is the absence of the key (`DocProject`).
      ...(row.skipUnchangedInstall !== undefined &&
      p.bool(row.skipUnchangedInstall, `${here}.skipUnchangedInstall`) === false
        ? { skipUnchangedInstall: false as const }
        : {}),
      color: color ?? '#4C8DFF',
      envFile: p.oneOf<EnvFileMode>(row.envFile, `${here}.envFile`, ENV_FILE_MODES) ?? 'auto',
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0,
      env: parseEnv(p, row.env, `${here}.env`),
      api: parseApi(p, row.api, `${here}.api`)
    } satisfies DocProject;
    out.push(repo);
  }
  const seen = new Set<string>();
  for (const member of members) {
    if (!remotes.has(member.remote)) {
      p.add(`${member.at}.subPath`, `This is a folder of ${member.remote}, which this workspace does not hold. Add the repository too.`);
    }
    const key = `${member.remote}\n${member.subPath}`;
    if (seen.has(key)) p.add(`${member.at}.subPath`, `That folder of ${member.remote} is already a project here.`);
    seen.add(key);
  }
  return out;
}

/** `services/api` — relative, forward slashes, no step outside the folder a member picks. */
function relativePath(p: Problems, value: unknown, at: string): string | null {
  const text = p.text(value, at, { min: 1, max: MAX_REL_PATH_CHARS });
  if (text === null) return null;
  const segments = text.split('/');
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused.
  const unsafe = /[\\:*?"<>|\x00-\x1f]/;
  if (text.startsWith('/') || segments.some((segment) => !segment || segment === '.' || segment === '..' || unsafe.test(segment))) {
    p.add(at, 'A folder under the one each member picks — relative, with forward slashes, and never above it.');
    return null;
  }
  return text;
}

function parseEnv(p: Problems, value: unknown, at: string): DocEnvVar[] {
  const out: DocEnvVar[] = [];
  for (const [index, raw] of p.list(value, at, MAX_ENV_VARS).entries()) {
    const row = p.object(raw, `${at}[${index}]`, ['key', 'value', 'isSecret', 'scope']);
    if (!row) continue;
    const key = p.text(row.key, `${at}[${index}].key`, { min: 1, max: MAX_ENV_KEY_CHARS });
    const here = key ? `${at}[${key}]` : `${at}[${index}]`;
    if (key !== null && !ENV_KEY.test(key)) p.add(here, 'A variable name has no spaces, slashes or `=`.');
    if (key !== null && out.some((env) => env.key === key)) p.add(here, 'Two variables share this name.');
    const isSecret = p.bool(row.isSecret, `${here}.isSecret`) ?? false;
    let secretValue: string | null = null;
    if (isSecret) {
      if (row.value !== null) p.add(`${here}.value`, 'A secret travels apart from the document — its value here must be null.');
    } else secretValue = p.text(row.value, `${here}.value`, { max: MAX_ENV_VALUE_CHARS });
    // Read, then dropped: whatever an older app wrote, a variable reaches
    // install and start alike now (`EnvScope`). Still checked, so a document
    // that is not one of ours is still refused.
    p.oneOf<EnvScope>(row.scope, `${here}.scope`, ENV_SCOPES);
    out.push({ key: key ?? '', value: isSecret ? null : (secretValue ?? ''), isSecret, scope: 'both' });
  }
  return out;
}

/* ── the API console ────────────────────────────────────────────────────── */

function parseApi(p: Problems, value: unknown, at: string): DocApi {
  const empty: DocApi = { folders: [], requests: [], environments: [], variables: [], flows: [] };
  const row = p.object(value, at, ['folders', 'requests', 'environments', 'variables', 'flows']);
  if (!row) return empty;
  const folders = parseFolders(p, row.folders, `${at}.folders`);
  const requests = parseRequests(p, row.requests, `${at}.requests`, folders);
  if (folders.length + requests.length > MAX_COLLECTION_ROWS) {
    p.add(at, `A collection holds at most ${MAX_COLLECTION_ROWS} folders and calls.`);
  }
  const environments = parseEnvironments(p, row.environments, `${at}.environments`);
  const variables = parseVariables(p, row.variables, `${at}.variables`, environments);
  const flows = parseFlows(p, row.flows, `${at}.flows`);
  return { folders, requests, environments, variables, flows };
}

function parseFolders(p: Problems, value: unknown, at: string): DocApiFolder[] {
  const out: DocApiFolder[] = [];
  for (const [index, raw] of p.list(value, at, MAX_COLLECTION_ROWS).entries()) {
    const row = p.object(raw, `${at}[${index}]`, ['id', 'parentId', 'name', 'sortOrder']);
    if (!row) continue;
    const here = `${at}[${typeof row.name === 'string' ? row.name : index}]`;
    out.push({
      id: p.id(row.id, `${here}.id`, out) ?? '',
      parentId: row.parentId === null ? null : p.id(row.parentId, `${here}.parentId`),
      name: p.text(row.name, `${here}.name`, { min: 1, max: MAX_FOLDER_NAME_CHARS, trim: true }) ?? '',
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0
    });
  }
  const byId = new Map(out.map((folder) => [folder.id, folder]));
  for (const folder of out) {
    if (folder.parentId !== null && !byId.has(folder.parentId)) {
      p.add(`${at}[${folder.name}]`, 'Its parent folder is not in the collection.');
      continue;
    }
    // How many folders sit above this one, walked up by parent — and a parent
    // met twice is a folder inside itself, which no editor can draw.
    let depth = 0;
    let cycle = false;
    const seen = new Set<string>([folder.id]);
    for (let parent = byId.get(folder.parentId ?? ''); parent; parent = byId.get(parent.parentId ?? '')) {
      if (seen.has(parent.id)) {
        cycle = true;
        break;
      }
      seen.add(parent.id);
      depth += 1;
    }
    if (cycle) p.add(`${at}[${folder.name}]`, 'Folders cannot contain themselves.');
    else if (depth >= MAX_FOLDER_DEPTH) p.add(`${at}[${folder.name}]`, `Folders nest ${MAX_FOLDER_DEPTH} deep.`);
  }
  return out;
}

const REQUEST_KEYS = ['id', 'folderId', 'flowId', 'name', 'method', 'url', 'headers', 'bodyKind', 'body', 'form', 'sortOrder', 'doc', 'scripts'] as const;

function parseRequests(p: Problems, value: unknown, at: string, folders: DocApiFolder[]): DocApiRequest[] {
  const out: DocApiRequest[] = [];
  const folderIds = new Set(folders.map((folder) => folder.id));
  for (const [index, raw] of p.list(value, at, MAX_COLLECTION_ROWS).entries()) {
    const row = p.object(raw, `${at}[${index}]`, REQUEST_KEYS);
    if (!row) continue;
    const here = `${at}[${typeof row.name === 'string' ? row.name : index}]`;
    const folderId = row.folderId === null ? null : p.id(row.folderId, `${here}.folderId`);
    if (folderId !== null && !folderIds.has(folderId)) p.add(`${here}.folderId`, 'Its folder is not in the collection.');
    out.push({
      id: p.id(row.id, `${here}.id`, out) ?? '',
      folderId,
      flowId: row.flowId === null ? null : p.id(row.flowId, `${here}.flowId`),
      name: p.text(row.name, `${here}.name`, { max: MAX_REQUEST_NAME_CHARS }) ?? '',
      method: p.oneOf<HttpMethod>(row.method, `${here}.method`, HTTP_METHODS) ?? 'GET',
      url: p.text(row.url, `${here}.url`, { max: MAX_FIELD_CHARS }) ?? '',
      headers: parseHeaders(p, row.headers, `${here}.headers`),
      bodyKind: p.oneOf<'text' | 'form'>(row.bodyKind, `${here}.bodyKind`, ['text', 'form']) ?? 'text',
      body: p.text(row.body, `${here}.body`, { max: MAX_BODY_CHARS }) ?? '',
      form: parseForm(p, row.form, `${here}.form`),
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0,
      // Only where there is some — read back as it was written (`DocApiRequest.doc`).
      ...(row.doc === undefined || row.doc === ''
        ? {}
        : { doc: p.text(row.doc, `${here}.doc`, { max: MAX_REQUEST_DOC_CHARS }) ?? '' }),
      // The same bargain as `doc`: only a call that has one carries the key.
      ...(row.scripts === undefined ? {} : parseScripts(p, row.scripts, `${here}.scripts`))
    });
  }
  return out;
}

function parseScripts(p: Problems, value: unknown, at: string): { scripts?: RequestScripts } {
  const row = p.object(value, at, ['pre', 'post']);
  if (!row) return {};
  const scripts = {
    pre: p.text(row.pre ?? '', `${at}.pre`, { max: MAX_SCRIPT_CHARS }) ?? '',
    post: p.text(row.post ?? '', `${at}.post`, { max: MAX_SCRIPT_CHARS }) ?? ''
  };
  return scripts.pre.trim() || scripts.post.trim() ? { scripts } : {};
}

function parseHeaders(p: Problems, value: unknown, at: string): HttpHeader[] {
  return p.list(value, at, MAX_ROWS_IN_A_REQUEST).flatMap((raw, index) => {
    const row = p.object(raw, `${at}[${index}]`, ['name', 'value', 'enabled']);
    if (!row) return [];
    return [
      {
        name: p.text(row.name, `${at}[${index}].name`, { max: MAX_FIELD_CHARS }) ?? '',
        value: p.text(row.value, `${at}[${index}].value`, { max: MAX_FIELD_CHARS }) ?? '',
        enabled: p.bool(row.enabled, `${at}[${index}].enabled`) ?? true
      }
    ];
  });
}

function parseForm(p: Problems, value: unknown, at: string): FormPart[] {
  return p.list(value, at, MAX_ROWS_IN_A_REQUEST).flatMap((raw, index) => {
    const here = `${at}[${index}]`;
    const row = p.object(raw, here, ['name', 'kind', 'value', 'file', 'enabled']);
    if (!row) return [];
    const kind = p.oneOf<'text' | 'file'>(row.kind, `${here}.kind`, ['text', 'file']) ?? 'text';
    let file: FormPart['file'] = null;
    if (row.file !== null) {
      const described = p.object(row.file, `${here}.file`, ['name', 'type', 'size', 'data']);
      if (described) {
        // The collection describes a call; it has never kept a file's bytes (§5.11).
        if (described.data !== '') p.add(`${here}.file.data`, 'A saved call keeps a file part’s name, never its bytes.');
        file = {
          name: p.text(described.name, `${here}.file.name`, { max: MAX_FIELD_CHARS }) ?? '',
          type: p.text(described.type, `${here}.file.type`, { max: 200 }) ?? '',
          size: 0,
          data: ''
        };
      }
    }
    return [
      {
        name: p.text(row.name, `${here}.name`, { max: MAX_FIELD_CHARS }) ?? '',
        kind,
        value: p.text(row.value, `${here}.value`, { max: MAX_BODY_CHARS }) ?? '',
        file: kind === 'file' ? file : null,
        enabled: p.bool(row.enabled, `${here}.enabled`) ?? true
      }
    ];
  });
}

function parseEnvironments(p: Problems, value: unknown, at: string): DocApiEnvironment[] {
  const out: DocApiEnvironment[] = [];
  for (const [index, raw] of p.list(value, at, MAX_ENVIRONMENTS).entries()) {
    const row = p.object(raw, `${at}[${index}]`, ['id', 'name', 'sortOrder']);
    if (!row) continue;
    out.push({
      id: p.id(row.id, `${at}[${index}].id`, out) ?? '',
      name: p.text(row.name, `${at}[${index}].name`, { min: 1, max: MAX_ENVIRONMENT_NAME_CHARS, trim: true }) ?? '',
      sortOrder: p.int(row.sortOrder, `${at}[${index}].sortOrder`, 0, MAX_SORT) ?? 0
    });
  }
  return out;
}

function parseVariables(p: Problems, value: unknown, at: string, environments: DocApiEnvironment[]): DocApiVariable[] {
  const rows: DocApiVariable[] = [];
  for (const [index, raw] of p.list(value, at, MAX_VARIABLES).entries()) {
    const row = p.object(raw, `${at}[${index}]`, ['id', 'key', 'value', 'values', 'isSecret', 'sortOrder']);
    if (!row) continue;
    const here = `${at}[${typeof row.key === 'string' ? row.key : index}]`;
    const isSecret = p.bool(row.isSecret, `${here}.isSecret`) ?? false;
    const values: Record<string, string> = {};
    if (!isPlainObject(row.values)) p.add(`${here}.values`, 'Expected an object of environment to value.');
    else {
      for (const [environmentId, entry] of Object.entries(row.values)) {
        if (!environments.some((environment) => environment.id === environmentId)) {
          p.add(`${here}.values`, `No environment ${environmentId} in this console.`);
        } else if (typeof entry !== 'string' || entry.length > MAX_VALUE_CHARS) {
          p.add(`${here}.values`, `A value is text of at most ${MAX_VALUE_CHARS} characters.`);
        } else values[environmentId] = entry;
      }
    }
    if (isSecret && (row.value !== null || Object.keys(values).length)) {
      p.add(here, 'A secret travels apart from the document — its values here must be empty.');
    }
    rows.push({
      id: p.id(row.id, `${here}.id`, rows) ?? '',
      key: p.text(row.key, `${here}.key`, { min: 1, max: 60 }) ?? '',
      value: isSecret ? null : (p.text(row.value, `${here}.value`, { max: MAX_VALUE_CHARS }) ?? ''),
      values: isSecret ? {} : values,
      isSecret,
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0
    });
  }
  if (p.failed) return rows;
  // The editor's own rule, so a key it would refuse to save is one a publish
  // cannot carry: the grammar, uniqueness, and the environment ids.
  const sanitized = p.attempt(at, () =>
    sanitizeVariables({
      environments: environments.map(({ id, name }) => ({ id, name })),
      variables: rows.map((row) => ({ id: row.id, key: row.key, value: row.value ?? '', values: row.values, isSecret: row.isSecret }))
    })
  );
  if (!sanitized) return rows;
  return rows.map((row, index) => {
    const clean = sanitized.variables[index];
    if (clean && clean.key !== row.key) p.add(`${at}[${row.key}].key`, `Stored as ${clean.key}.`);
    return row;
  });
}

function parseFlows(p: Problems, value: unknown, at: string): DocApiFlow[] {
  const out: DocApiFlow[] = [];
  for (const [index, raw] of p.list(value, at, MAX_FLOWS).entries()) {
    const row = p.object(raw, `${at}[${index}]`, ['id', 'name', 'nodes', 'edges', 'sortOrder']);
    if (!row) continue;
    const here = `${at}[${typeof row.name === 'string' ? row.name : index}]`;
    const graph = p.attempt(here, () =>
      sanitizeFlow({ nodes: row.nodes as ApiFlowNode[], edges: row.edges as ApiFlowEdge[] })
    );
    out.push({
      id: p.id(row.id, `${here}.id`, out) ?? '',
      name: p.text(row.name, `${here}.name`, { min: 1, max: MAX_FLOW_NAME_CHARS, trim: true }) ?? '',
      nodes: graph?.nodes ?? [],
      edges: graph?.edges ?? [],
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0
    });
  }
  return out;
}

/* ── the rest of the workspace ──────────────────────────────────────────── */

function parseConfig(p: Problems, value: unknown): DocConfig | null {
  const known = [
    ...Object.keys(normalizeWorkspaceConfig('document', {})).filter((key) => key !== 'workspaceId'),
    ...SPARSE_CONFIG_KEYS
  ];
  const row = p.object(value, 'config', known);
  if (!row) return null;
  const { workspaceId: _ignored, ...kept } = normalizeWorkspaceConfig('document', row);
  for (const key of known) {
    if (row[key] === undefined) continue;
    // A sparse override is absent when inherited; `null`, as a patch writes it, says the same.
    if ((key === 'autoLand' || key === 'audit') && row[key] === null) continue;
    // An empty sparse list is the same answer as its absence, which is how it is kept.
    if ((SPARSE_CONFIG_KEYS as readonly string[]).includes(key) && Array.isArray(row[key]) && !(row[key] as unknown[]).length) continue;
    if (key === 'localServices') {
      const refused = localServicesInputProblem(row[key]);
      if (refused) {
        p.add('config.localServices', refused);
        continue;
      }
    }
    if (key === 'rulesRepository') {
      const problems: string[] = [];
      parseRulesRepoRef(row[key], 'config.rulesRepository', problems);
      for (const problem of problems) {
        const [at, ...why] = problem.split(': ');
        p.add(at, why.join(': '));
      }
      // Sound, it is kept as normalized — a trimmed remote, an empty branch as null.
      continue;
    }
    const wanted = JSON.stringify(row[key]);
    const stored = JSON.stringify((kept as Record<string, unknown>)[key]);
    if (wanted !== stored) p.add(`config.${key}`, 'That is not a value Airlock keeps for this setting.');
  }
  return kept;
}

/**
 * An organization's workflows (§5.25), read from anything that crossed a
 * network — the same refusals a workspace document gets, for the same reason.
 */
export function parseOrgWorkflows(input: unknown): ParsedOrgWorkflows {
  const p = new Problems();
  if (documentBytes(input) > MAX_DOCUMENT_BYTES) {
    return { ok: false, problems: [`An organization's workflows hold at most ${MAX_DOCUMENT_BYTES / 1024} KB.`] };
  }
  const root = p.object(input, '', ['workflows']);
  if (!root) return { ok: false, problems: p.messages };
  const workflows = parseWorkflows(p, root.workflows);
  return p.failed ? { ok: false, problems: p.messages } : { ok: true, document: { workflows } };
}

function parseWorkflows(p: Problems, value: unknown): DocWorkflow[] {
  const out: DocWorkflow[] = [];
  for (const [index, raw] of p.list(value, 'workflows', MAX_WORKFLOWS).entries()) {
    const row = p.object(raw, `workflows[${index}]`, ['id', 'name', 'steps', 'edges', 'sortOrder']);
    if (!row) continue;
    const here = `workflows[${typeof row.name === 'string' ? row.name : index}]`;
    const graph = p.attempt(here, () =>
      sanitizeWorkflow({ steps: row.steps as WorkflowStep[], edges: row.edges as WorkflowEdge[] })
    );
    out.push({
      id: p.id(row.id, `${here}.id`, out) ?? '',
      name: p.text(row.name, `${here}.name`, { min: 1, max: MAX_WORKFLOW_NAME_CHARS, trim: true }) ?? '',
      steps: graph?.steps ?? [],
      edges: graph?.edges ?? [],
      sortOrder: p.int(row.sortOrder, `${here}.sortOrder`, 0, MAX_SORT) ?? 0
    });
  }
  return out;
}

function parseTaskSources(p: Problems, value: unknown): DocTaskSource[] {
  const out: DocTaskSource[] = [];
  const at = 'taskSources';
  for (const [index, raw] of p.list(value, at, MAX_TASK_SOURCES).entries()) {
    const here = `${at}[${index}]`;
    const row = p.object(raw, here, ['id', 'kind', 'host', 'projectKey', 'doneStatus', 'importStatuses', 'laneStatuses']);
    if (!row) continue;
    const host = typeof row.host === 'string' ? jiraHost(row.host) : undefined;
    if (!host || host !== row.host) p.add(`${here}.host`, 'A bare host, like example.atlassian.net.');
    const laneStatuses: LaneStatuses = {};
    if (!isPlainObject(row.laneStatuses)) p.add(`${here}.laneStatuses`, 'Expected an object of lane to status.');
    else {
      for (const [lane, status] of Object.entries(row.laneStatuses)) {
        if (!(TASK_LANES as readonly string[]).includes(lane)) p.add(`${here}.laneStatuses`, `${lane} is not a lane.`);
        else if (typeof status !== 'string' || status.length > MAX_TASK_TEXT) p.add(`${here}.laneStatuses.${lane}`, 'A status name.');
        else {
          // A status stands for one lane (0240/one-lane-per-status), compared as the sync compares it.
          const key = status.trim().toLowerCase();
          const first = key
            ? Object.entries(laneStatuses).find(([, other]) => other?.trim().toLowerCase() === key)
            : undefined;
          const problem = first && `“${status.trim()}” already stands for ${first[0]} — a status moves one lane.`;
          if (problem) p.add(`${here}.laneStatuses.${lane}`, problem);
          else laneStatuses[lane as keyof LaneStatuses] = status;
        }
      }
    }
    const source: DocTaskSource = {
      id: p.id(row.id, `${here}.id`, out) ?? '',
      kind: p.oneOf<TaskSourceKind>(row.kind, `${here}.kind`, TASK_SOURCE_KINDS) ?? 'jira',
      host: host ?? '',
      projectKey: p.text(row.projectKey, `${here}.projectKey`, { min: 1, max: 100 }) ?? '',
      doneStatus: p.text(row.doneStatus, `${here}.doneStatus`, { max: MAX_TASK_TEXT }) ?? '',
      importStatuses: p
        .list(row.importStatuses, `${here}.importStatuses`, MAX_IMPORT_STATUSES)
        .flatMap((status, i) => {
          const text = p.text(status, `${here}.importStatuses[${i}]`, { max: MAX_TASK_TEXT });
          return text === null ? [] : [text];
        }),
      laneStatuses
    };
    if (out.some((other) => other.kind === source.kind && other.host === source.host && other.projectKey === source.projectKey)) {
      p.add(here, 'That ticket project is already routed here.');
    }
    out.push(source);
  }
  return out;
}

/* ── the collector ──────────────────────────────────────────────────────── */

class Problems {
  readonly messages: string[] = [];
  newer = false;

  get failed(): boolean {
    return this.messages.length > 0;
  }

  add(at: string, message: string): void {
    if (this.messages.length < MAX_PROBLEMS) this.messages.push(at ? `${at}: ${message}` : message);
  }

  result(document: WorkspaceDocument | null): ParsedDocument {
    if (document && !this.failed) return { ok: true, document };
    if (!this.failed) this.add('', 'That is not a project document.');
    return { ok: false, problems: [...this.messages], newer: this.newer };
  }

  object(value: unknown, at: string, keys: readonly string[]): Record<string, unknown> | null {
    if (!isPlainObject(value)) {
      this.add(at, 'Expected an object.');
      return null;
    }
    for (const key of Object.keys(value)) {
      if (keys.includes(key)) continue;
      // A field this version has never heard of was written by one that has, so
      // this is the same answer a raised `schema` deserves: update, rather than
      // "could not be read". Only a newer Airlock writes into this document —
      // the app and the Worker share this reader, and neither invents a key.
      //
      // It does not return early the way the schema gate does. A document may
      // be unreadable for several reasons at once, and naming only the first
      // sends somebody back for the second.
      this.newer = true;
      this.add(at ? `${at}.${key}` : key, 'Not a field this version of Airlock knows. Update Airlock to open it.');
    }
    return value;
  }

  list(value: unknown, at: string, max: number): unknown[] {
    if (!Array.isArray(value)) {
      this.add(at, 'Expected a list.');
      return [];
    }
    if (value.length > max) {
      this.add(at, `At most ${max}.`);
      return value.slice(0, max);
    }
    return value;
  }

  text(value: unknown, at: string, rule: { min?: number; max: number; trim?: boolean }): string | null {
    if (typeof value !== 'string') {
      this.add(at, 'Expected text.');
      return null;
    }
    const text = rule.trim ? value.trim() : value;
    if (text.length > rule.max) {
      this.add(at, `At most ${rule.max} characters.`);
      return null;
    }
    if (text.length < (rule.min ?? 0)) {
      this.add(at, 'Cannot be empty.');
      return null;
    }
    return text;
  }

  nullableText(value: unknown, at: string, max: number): string | null {
    if (value === null) return null;
    const text = this.text(value, at, { max });
    return text === '' ? null : text;
  }

  bool(value: unknown, at: string): boolean | null {
    if (typeof value === 'boolean') return value;
    this.add(at, 'Expected true or false.');
    return null;
  }

  int(value: unknown, at: string, min: number, max: number): number | null {
    if (typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max) return value;
    this.add(at, `Expected a whole number from ${min} to ${max}.`);
    return null;
  }

  oneOf<T extends string>(value: unknown, at: string, allowed: readonly T[]): T | null {
    if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) return value as T;
    this.add(at, `Expected one of ${allowed.join(', ')}.`);
    return null;
  }

  id(value: unknown, at: string, siblings?: { id: string }[]): string | null {
    if (typeof value !== 'string' || !ROW_ID.test(value)) {
      this.add(at, 'Not an id Airlock mints.');
      return null;
    }
    if (siblings?.some((row) => row.id === value)) {
      this.add(at, 'Two rows share this id.');
      return null;
    }
    return value;
  }

  /** A sanitizer's own sentence, or a plain one when what threw was the shape rather than the rule. */
  attempt<T>(at: string, run: () => T): T | null {
    try {
      return run();
    } catch (error) {
      this.add(at, error instanceof TypeError || !(error instanceof Error) ? 'That is not a shape an editor saves.' : error.message);
      return null;
    }
  }
}
